import * as Sentry from '@sentry/react';

interface SentryFrontendConfig {
  dsn: string;
  apiEndpoint: string;
  stage: string;
  version?: string;
}

export function initSentryFrontend(config: SentryFrontendConfig) {
  if (config.stage === 'local') {
    return; // Don't initialize Sentry for local development
  }

  Sentry.init({
    release: config.version,
    dsn: config.dsn,
    environment: config.stage,
    
    // Enable distributed tracing to backend
    tracePropagationTargets: [config.apiEndpoint],
    
    integrations: [
      Sentry.browserTracingIntegration({
        // Filter out unwanted third-party HTTP requests
        shouldCreateSpanForRequest: (url) => {
          // Allow our own API calls for distributed tracing
          if (url.includes(config.apiEndpoint)) {
            return true;
          }
          
          // Block specific third-party services that create noise
          const blockedDomains = [
            'fast.trychameleon.com',
            'fast.chmln-cdn.com',
            'www.loom.com',
            'loom.com',
            'trychameleon.com',
            'chmln-cdn.com',
          ];
          
          return !blockedDomains.some(domain => url.includes(domain));
        },
      }),
      Sentry.replayIntegration(),
    ],
    
    // Environment-based sampling rates - errors are ALWAYS captured regardless
    tracesSampleRate: config.stage === 'prod' ? 0.1 : 0.0, // Only trace in production
    replaysSessionSampleRate: config.stage === 'prod' ? 0.01 : 0.0, // Only replay in production  
    replaysOnErrorSampleRate: 1.0, // Always capture errors with replay (all environments)
    
    // Common error filtering
    ignoreErrors: [
      // Random plugins/extensions
      'top.GLOBALS',
      'originalCreateNotification',
      'canvas.contentDocument',
      'MyApp_RemoveAllHighlights',
      'http://tt.epicplay.com',
      "Can't find variable: ZiteReader",
      'jigsaw is not defined',
      'ComboSearch is not defined',
      'http://loading.retry.widdit.com/',
      'atomicFindClose',
      // Facebook issues
      'fb_xd_fragment',
      'bmi_SafeAddOnload',
      'EBCallBackMessageReceived',
      // Conduit toolbar
      'conduitPage',
      // Chameleon script errors
      "Can't find variable: EmptyRanges",
    ],
    
    denyUrls: [
      // Facebook
      /graph\.facebook\.com/i,
      /connect\.facebook\.net\/en_US\/all\.js/i,
      // Woopra
      /eatdifferent\.com\.woopra-ns\.com/i,
      /static\.woopra\.com\/js\/woopra\.js/i,
      // Browser extensions
      /extensions\//i,
      /^chrome:\/\//i,
      /^chrome-extension:\/\//i,
      // Other plugins
      /127\.0\.0\.1:4001\/isrunning/i,
      /webappstoolbarba\.texthelp\.com\//i,
      /metrics\.itunes\.apple\.com\.edgesuite\.net\//i,
      /fast\.trychameleon\.com/i,
    ],
  });
}